
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { DentalAppointmentForm } from "@/components/DentalAppointmentForm";
import { Clock, Calendar, CheckCircle } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { PageTitle } from "@/components/PageTitle";

const DentalAppointmentBooking = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <PageTitle title="Book an Appointment – Asrani Dental Clinic" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Schedule Your Visit
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Book Your Dental Appointment
            </h1>
            <p className="text-lg text-dental-muted">
              Take the first step towards a healthier smile. Schedule your dental appointment with our experienced team today.
            </p>
          </div>
        </div>
      </section>
      
      {/* Appointment Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <SectionTitle 
              title="Request an Appointment" 
              subtitle="Easy Booking"
              description="Fill out the form below and we'll send you a confirmation via WhatsApp."
              alignment="center"
            />
            
            <div className="mt-12 bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100">
              <DentalAppointmentForm />
            </div>
          </div>
          
          {/* Clinic Information */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Office Hours */}
            <div className="bg-dental-offwhite rounded-xl p-6">
              <h3 className="text-xl font-serif font-semibold mb-4 flex items-center">
                <Clock className="mr-2 text-dental-highlight" size={20} />
                Clinic Hours
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-dental-muted">Monday - Friday:</span>
                  <span className="font-medium">9 AM - 7 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dental-muted">Saturday:</span>
                  <span className="font-medium">9 AM - 5 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dental-muted">Sunday:</span>
                  <span className="font-medium">Closed</span>
                </div>
              </div>
            </div>
            
            {/* Location */}
            <div className="bg-dental-offwhite rounded-xl p-6">
              <h3 className="text-xl font-serif font-semibold mb-4 flex items-center">
                <Calendar className="mr-2 text-dental-highlight" size={20} />
                Location
              </h3>
              <p className="text-dental-muted text-sm">
                Asrani Dental Clinic<br/>
                123 Dental Street<br/>
                Nagpur, Maharashtra 440001<br/>
                <a onClick={(e) => { e.preventDefault(); (window as any).gtag_report_conversion && (window as any).gtag_report_conversion("tel:+917020757368"); }} href="tel:+917020757368" className="text-dental-highlight">
                  +91 7020757368
                </a>
              </p>
            </div>
            
            {/* Why Choose Us */}
            <div className="bg-dental-offwhite rounded-xl p-6">
              <h3 className="text-xl font-serif font-semibold mb-4 flex items-center">
                <CheckCircle className="mr-2 text-dental-highlight" size={20} />
                Why Choose Us
              </h3>
              <ul className="text-dental-muted text-sm space-y-2">
                <li className="flex items-center">
                  <CheckCircle className="text-dental-highlight mr-2 h-3 w-3 flex-shrink-0" />
                  <span>Advanced dental technology</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="text-dental-highlight mr-2 h-3 w-3 flex-shrink-0" />
                  <span>Experienced dental specialists</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="text-dental-highlight mr-2 h-3 w-3 flex-shrink-0" />
                  <span>Comfortable, pain-free procedures</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
      <Toaster />
    </div>
  );
};

export default DentalAppointmentBooking;
